import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useEditorStore } from '~/shell/Store';
import { useMediaFetch } from '../hooks/useMediaFetch';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import SearchHeader from '../components/SearchHeader';
import MediaGrid from '../components/MediaGrid';
import { MediaFile } from '../components/MediaGrid';
import useDebounce from '../hooks/useDebounce';

const Container = styled.div`
  h3 {
    font-family: ralewayBold;
    font-size: 16px;
    font-weight: unset !important;
    line-height: 24px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
    padding-bottom: 10px;
  }
`;

type UnsplashSectionProps = {
  onSelect: (file: MediaFile) => void;
};

const UnsplashSection: React.FC<UnsplashSectionProps> = ({ onSelect }) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const { apiService } = useEditorStore();
  const {
    files,
    loading,
    noResults,
    fetchMedia,
    resetState,
    setLoadingValue,
    clearFiles
  } = useMediaFetch('image', 'unsplash');

  const loadMore = async () => {
    if (!canLoadMore) return;
    const hasMore = await fetchMedia(page, debouncedSearch);
    setCanLoadMore(hasMore ?? false);
  };

  const { page, setPage, loadMoreRef, setCanLoadMore, canLoadMore } =
    useInfiniteScroll(loadMore);

  useEffect(() => {
    resetState();
    setPage(1);
    setCanLoadMore(true);
  }, [debouncedSearch]);

  const handleSelect = async (file: MediaFile) => {
    if (!apiService) return;

    setCanLoadMore(false);
    clearFiles();
    setLoadingValue(true);

    try {
      if (!('width' in file)) {
        const response = await apiService.downloadUnsplashImage(
          `${file.id}`,
          file.name || ''
        );
        if (response?.picture) {
          onSelect(response.picture);
          setLoadingValue(false);
        }
      } else {
        onSelect(file);
        setLoadingValue(false);
      }
    } catch (error) {
      console.error('Error downloading Unsplash image:', error);
      setLoadingValue(false);
    }
  };

  return (
    <Container>
      <h3>Unsplash</h3>
      <SearchHeader
        placeholder="Search images"
        value={search}
        onChange={setSearch}
      />
      {files && search === '' && files.length > 0 && <h3>Recently Used</h3>}
      {files && search.length > 0 && files.length > 0 && (
        <h3>Search results for "{search}"</h3>
      )}
      {noResults && search && search.trim() !== '' && (
        <h3>No results found for "{search}"</h3>
      )}
      <MediaGrid
        files={files}
        loading={loading}
        type="image"
        onSelect={handleSelect}
        loadMoreRef={loadMoreRef}
        showNoResults={noResults}
        searchQuery={search}
      />
    </Container>
  );
};

export default UnsplashSection;
